import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/api/dashboard.service';
import { walletService } from '../services/api/wallet.service';
import { DashboardData } from "../types/application.types";
import { mainService } from '../services/api/main.service';
import { walletTransactionService } from '../services/api/wallet-transaction.service';
import { cardService } from '../services/api/card.service';
import { useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from './../store'
export const useMainWallets = (virtualDash: boolean) => {
    return useQuery({
        queryKey: ['mainWallets', virtualDash],
        queryFn: () => mainService.getMainWallets(virtualDash),
        staleTime: 600000,
        refetchOnWindowFocus: false,
    });
};
export const useCryptoWallets = () => {
    return useQuery({
        queryKey: ['cryptoWallets'],
        queryFn: async () => {            
            const data = await walletService.getCryptoWallets();           
            return data;
        },
        staleTime: 600000,
        refetchOnWindowFocus: false,
        retry: 1
    });
};
export const useMastercardCards = () => {
    return useQuery({
        queryKey: ['mastercard-cards'],
        queryFn: async () => {
            const data = await cardService.getMasterCardData();
            if (data?.success && data?.data?.data && Array.isArray(data?.data?.data)) {
                return data.data.data;
            }
            return [];
        },
        staleTime: 600000, // 10 minute
        refetchOnWindowFocus: true,

    });
};



// Helper validator (runtime check)
function isValidDashboardData(data: any): data is DashboardData {
    return (
        data &&
        Array.isArray(data.wallets) &&
        typeof data.totalWealth === 'number' &&
        typeof data.fiatBalance === 'number' &&
        typeof data.cryptoBalance === 'number' &&
        typeof data.cardBalance === 'number'
    );
}

export const useBitcoinFees = (selectedCurrency: string) => {
    return useQuery({
        queryKey: ['bitcoin-fees'],
        queryFn: () => walletTransactionService.getBitcoinFees(),
        enabled: selectedCurrency === 'BTC',
        staleTime: 300000, // 5 minutos
        refetchOnWindowFocus: false,
        retry: 2
    });
};

export const useEthereumFees = (selectedCurrency: string) => {
    return useQuery({
        queryKey: ['ethereum-fees'],
        queryFn: () => walletTransactionService.getEthereumGasFees(),
        enabled: selectedCurrency === 'eth' || selectedCurrency === 'usdt_erc20' || selectedCurrency === 'usdc_erc20',
        staleTime: 300000, // 5 minutos
        refetchOnWindowFocus: false,
        retry: 2
    });
};

export const useBnbFees = (selectedCurrency: string) => {
    return useQuery({
        queryKey: ['bnb-fees'],
        queryFn: () => walletTransactionService.getBnbFees(),
        enabled: selectedCurrency === 'bnb' || selectedCurrency === 'usdt_bep20',
        staleTime: 300000, // 5 minutos
        refetchOnWindowFocus: false,
        retry: 2
    });
};

export const useTronFees = (selectedCurrency: string, selectedWallet:string) => {
    return useQuery({
        queryKey: ['tron-fees', selectedWallet],
        queryFn: async () => walletTransactionService.getTronFees(selectedWallet),
        enabled: selectedWallet !== '' && (selectedCurrency === 'trx' || selectedCurrency === 'usdt_trc20'),
        retry: 2,
        refetchOnWindowFocus: false,
        staleTime: 300000 // 5 minutos
    });
};

export const useSolanaFees = (selectedCurrency: string) => {
    return useQuery({
        queryKey: ['solana-fees'],
        queryFn: () => walletTransactionService.getSolanaFees(),
        enabled: selectedCurrency === 'SOL' || selectedCurrency === 'usdt_spl' || selectedCurrency === 'usdc_spl',
        staleTime: 300000, // 5 minutos
        refetchOnWindowFocus: false,
        retry: 2
    });
};

export const useRailCounterpartyData = () => {
    return useQuery({
       queryKey: ['CounterpartiesData'],
           queryFn: () => mainService.getCounterpartiesData(),
           staleTime: 5 * 60 * 1000, // 5 minutes cache
           refetchOnWindowFocus: false,
           refetchOnMount: true, // Always refetch when component mounts
    });
};

export const useCardData = () =>{
    return useQuery({
        queryKey: ['verestro-cards'],
        queryFn: () => cardService.getCardData(),       
        staleTime: 60000, // 1 minuto
    })
}




// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()



